/* eslint-disable camelcase */
/**
 * Zod codec for a chat turn's citation list — Phase 1 Slice C of the
 * wire-codec boundary migration.
 *
 * The turn's citation list crosses BOTH transports:
 *   - SSE path (`done.citations`): raw snake_case off the wire
 *     (`chunk_id`, `source_id`, `source_type`, `cosine_distance`,
 *     `rerank_score`, …). `chatStream.ts` used to hand-camelize each
 *     citation inline so downstream renderers saw the FE camelCase
 *     `Citation` shape.
 *   - Replay path (`meta.citations` from
 *     `/api/chat/threads/<id>/messages/`): the global axios interceptor
 *     recursively camelizes the JSONField, so it arrives camelCase.
 *
 * Two transports, the same renderers (`CitationChip`, the Sources row,
 * the sidebar's `renderCitationChip`). This codec is the single
 * normalizer: `parseCitations(raw)` reads each field tolerant of both
 * spellings (`dualKey`) and emits the canonical FE `Citation[]`. Feed it
 * the SSE-raw list OR the axios-camelized list and it produces identical
 * output.
 *
 * Canonical output is the camelCase `Citation` (NOT snake_case) because
 * `Citation` is a long-established FE type the chip renderers + the
 * Staff/Chat metadata strip are built on — same rationale as the trace
 * codec. The codec converges the two transports onto that contract.
 *
 * NOTE — two distinct "citation" notions live in this codebase:
 *   - `Citation` (this file): the rich turn-level provenance record
 *     (`chunkId`, `sourceId`, `sourceType`, `anchor`, `url`, `quote`, …)
 *     that backs the Sources row + inline chips.
 *   - `CitationRef` (`tool-cards/shared/types.ts`, parsed by
 *     `citationRefSchema`): the slim `{label, href}` chip embedded
 *     INSIDE a card payload's `why` field.
 * They are not interchangeable; this codec owns the former.
 */
import { z } from "zod";

import { dualKey, isObject } from "@/api/codec";
import type { Citation, CitationSourceType } from "@/pages/Staff/Chat/types";

const asString = (v: unknown): string | undefined =>
  typeof v === "string" ? v : undefined;

const asNumber = (v: unknown): number | undefined =>
  typeof v === "number" ? v : undefined;

const SOURCE_TYPES: ReadonlySet<string> = new Set<CitationSourceType>([
  "statute",
  "dob_rule",
  "cc_kb",
  "user_doc",
  "data",
]);

/**
 * Parse a single raw citation (snake_case off SSE, camelCase off the
 * axios replay path) into the canonical FE {@link Citation}. Returns
 * `null` when the minimal identity (`chunk_id` + `source_id`) is
 * missing so the caller can drop a malformed entry rather than render a
 * broken chip.
 */
export const parseCitation = (raw: unknown): Citation | null => {
  if (!isObject(raw)) return null;
  const chunkId = asString(dualKey(raw, "chunk_id"));
  const sourceId = asString(dualKey(raw, "source_id"));
  // The turn-level citation contract requires a stable chunk + source
  // id to back the chip's popover lookup. A row missing both is noise.
  if (chunkId === undefined && sourceId === undefined) return null;
  const sourceTypeRaw = asString(dualKey(raw, "source_type"));
  const rerank = dualKey(raw, "rerank_score");
  return {
    chunkId: chunkId ?? "",
    sourceId: sourceId ?? "",
    sourceType:
      sourceTypeRaw !== undefined && SOURCE_TYPES.has(sourceTypeRaw)
        ? (sourceTypeRaw as CitationSourceType)
        : undefined,
    anchor: asString(dualKey(raw, "anchor")),
    url: asString(dualKey(raw, "url")),
    quote: asString(dualKey(raw, "quote")),
    cosineDistance: asNumber(dualKey(raw, "cosine_distance")),
    rerankScore: typeof rerank === "number" ? rerank : null,
    span: asString(dualKey(raw, "span")),
  };
};

/**
 * Parse a raw citation LIST into canonical `Citation[]`. Non-array
 * input → `[]`; malformed entries are dropped. This is the SINGLE
 * consumer-boundary normalizer for turn citations — both the SSE path
 * (`chatStream.ts` done event) and the replay path route through it.
 */
export const parseCitations = (raw: unknown): Citation[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(parseCitation)
    .filter((c): c is Citation => c !== null);
};

/**
 * Zod-wrapped list schema for the codec runners (`codecGet`), so a
 * `ReplayMessage` schema can compose citation parsing. Always succeeds
 * (drops malformed entries) — citations are a hover-grade affordance,
 * never a reason to fail the whole message parse.
 */
export const citationsSchema = z
  .unknown()
  .transform((raw): Citation[] => parseCitations(raw));
